import { FC } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'

// eslint-disable-next-line import/no-extraneous-dependencies
import { RightDrawer, Callout } from 'baby-design'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Gift } from 'baby-design/icons'

import { useReservation } from 'src/routes/(registry)/api/reservations'
import { Reservation } from 'src/types/reservation'
import GiftActions from 'src/routes/(registry)/registry/(gift-tracker-revamp)/components/GiftDetailsDrawer/GiftActions/GiftActions'
import { EventLocationType } from 'src/lib/tracking/shared/types'
import { track, withContextualizedTracking } from 'lib/analytics'
import GiftSummary from './GiftSummary'
import OrderDetails from './OrderDetails'

import css from './GiftDetailsDrawer.styles.scss'
import LoadingState from './LoadingState'

const Section: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={css.GiftDetailsDrawer__Section}>{children}</div>
)

const SurpriseGiftCallout: FC<{ name: string }> = ({ name }) => {
  const icon = <Gift />

  return (
    <Callout
      backgroundColor="eggplant"
      icon={icon}
      iconColor="eggplant"
      text={`${name} asked to keep this gift a surprise.`}
      title="Surprise gift"
      variant="multi-line"
    />
  )
}

export const Contents: FC<{
  onUnmarkedAsPurchased?: (reservation: Reservation) => void
  reservation: Reservation
  showContent: boolean
  showLoadingState: boolean
}> = ({
  onUnmarkedAsPurchased,
  reservation,
  showContent = true,
  showLoadingState = false,
}) => (
  <div className={css.GiftDetailsDrawer}>
    {showLoadingState && <LoadingState />}
    {showContent && (
      <div data-testid="GiftDetailsDrawer__Contents">
        <Section>
          {reservation.isSurprise && !!reservation.name && (
            <SurpriseGiftCallout name={reservation.name} />
          )}
          <GiftSummary reservation={reservation} />
        </Section>
        <Section>
          <OrderDetails reservation={reservation} />
        </Section>
        <GiftActions
          reservation={reservation}
          onUnmarkedAsPurchased={onUnmarkedAsPurchased}
        />
      </div>
    )}
  </div>
)

export const ContentsInDrawer: FC<{ reservationToken: string }> = ({
  reservationToken,
}) => {
  const modal = useModal()
  const {
    data: reservation,
    isLoading,
    isSuccess,
  } = useReservation(reservationToken)

  const handleUnmarkedAsPurchased = (res: Reservation) => {
    modal.resolve(res)
    modal.hide()
  }

  return (
    <RightDrawer
      handleClose={modal.hide}
      handleDismount={modal.remove}
      isOpen={modal.visible}
      title="Gift details"
    >
      <Contents
        reservation={reservation}
        showContent={!isLoading && isSuccess && !!reservation}
        showLoadingState={isLoading}
        onUnmarkedAsPurchased={handleUnmarkedAsPurchased}
      />
    </RightDrawer>
  )
}

const GiftDetailsDrawer = NiceModal.create(ContentsInDrawer)

export const showGiftDetailsDrawer = ({
  reservationToken,
  eventLocation = track.EventLocation.GIFT_TRACKER,
}: {
  reservationToken: string
  eventLocation?: EventLocationType
}): Promise<Reservation | undefined> => {
  const WrappedGiftDetailsDrawer = withContextualizedTracking({
    eventLocation,
  })(GiftDetailsDrawer)
  return NiceModal.show(WrappedGiftDetailsDrawer, { reservationToken })
}

export default GiftDetailsDrawer
